import { useEffect, useRef, useState } from "react";
import type {
  FieldResult,
  PageResult,
  RunResults,
  SavedSource,
} from "../shared/results";
import { api } from "./api";
import "./run-results.css";

const outcomeLabels = {
  imported: "Imported",
  unchanged: "Unchanged",
  skipped: "Skipped",
  failed: "Failed",
  partial: "Partially imported",
  pending: "In progress",
};
const fieldLabels = {
  captured: "Captured",
  not_extracted: "Not extracted",
  needs_attention: "Check this field",
  not_checked: "Not checked",
};
const stageLabels = {
  discovery: "Finding pages",
  scrape: "Scraping the page",
  structure: "Extracting event details",
  ingest: "Importing performances",
};
const runLabels = {
  running: "Processing",
  completed: "Finished",
  completed_with_issues: "Finished with issues",
  failed: "Could not finish",
  cancelled: "Stopped",
};
type Filter =
  | "all"
  | "attention"
  | "imported"
  | "unchanged"
  | "skipped"
  | "failed"
  | "pending";

function readableRole(value: string): string {
  const text = value.replaceAll("_", " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function FieldCard({ field }: { field: FieldResult }) {
  return (
    <div className={`result-field result-field-${field.status}`}>
      <div>
        <strong>{field.label}</strong>
        <span>{fieldLabels[field.status]}</span>
      </div>
      <p>{field.explanation}</p>
      {field.submittedValue && (
        <p className="result-submitted">
          {field.submittedValue === "Not sent" ? (
            "Not included in the import."
          ) : (
            <>
              <strong>Sent with the import:</strong> {field.submittedValue}
            </>
          )}
        </p>
      )}
    </div>
  );
}

function ResultPage({ page, runId }: { page: PageResult; runId: string }) {
  const [source, setSource] = useState<SavedSource | null>(null);
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [sourceOpen, setSourceOpen] = useState(false);
  async function readSource() {
    if (source) {
      setSourceOpen((value) => !value);
      return;
    }
    setSourceLoading(true);
    try {
      setSource(
        await api<SavedSource>(
          `/api/runs/${encodeURIComponent(runId)}/results/${encodeURIComponent(page.id)}/source`,
        ),
      );
      setSourceOpen(true);
      setSourceError(null);
    } catch (caught) {
      setSourceError(
        caught instanceof Error
          ? caught.message
          : "Saved page text could not be loaded.",
      );
    } finally {
      setSourceLoading(false);
    }
  }
  const missing = page.fields.filter(
    (field) => field.status !== "captured",
  ).length;
  return (
    <details className={`result-page result-page-${page.outcome}`}>
      <summary>
        <span className="result-page-title">
          <strong>{page.title || new URL(page.sourceUrl).pathname}</strong>
          <small>
            {new URL(page.sourceUrl).hostname} · Page {page.order}
          </small>
        </span>
        <span className="result-page-badges">
          {missing > 0 && (
            <span className="result-coverage-badge">
              {missing} fields to check
            </span>
          )}
          <span className={`result-outcome result-outcome-${page.outcome}`}>
            {outcomeLabels[page.outcome]}
          </span>
        </span>
      </summary>
      <div className="result-page-body">
        <div className="result-explanation">
          <p>{page.explanation}</p>
          {page.nextAction && (
            <p>
              <strong>Next step:</strong> {page.nextAction}
            </p>
          )}
          <small>
            {stageLabels[page.stage]}
            {page.scrapeAttempts && page.scrapeAttempts > 1
              ? ` · Scraping retried ${page.scrapeAttempts - 1} time(s)`
              : ""}
            {page.extractionAttempts && page.extractionAttempts > 1
              ? ` · Event extraction retried ${page.extractionAttempts - 1} time(s)`
              : ""}
            {page.reused ? " · Reused from the previous run" : ""}
          </small>
        </div>
        <a
          className="result-source-link"
          href={page.sourceUrl}
          target="_blank"
          rel="noreferrer"
        >
          Open source page ↗
        </a>
        {page.scraped && (
          <button
            type="button"
            className="result-read-source"
            disabled={sourceLoading}
            aria-expanded={sourceOpen}
            onClick={() => void readSource()}
          >
            {sourceLoading
              ? "Loading saved text…"
              : sourceOpen
                ? "Hide saved page text"
                : "Read saved page text"}
          </button>
        )}
        {sourceError && (
          <p className="result-error" role="alert">
            {sourceError}
          </p>
        )}
        {source && sourceOpen && (
          <section className="result-saved-source">
            <h4>What was scraped</h4>
            <p className="result-help">{source.explanation}</p>
            {source.text && <pre>{source.text}</pre>}
          </section>
        )}
        <ol className="result-page-steps" aria-label="Page progress">
          <li className={page.scraped ? "done" : ""}>
            {page.scraped ? "✓" : "—"} Page scraped
          </li>
          <li className={page.event ? "done" : ""}>
            {page.event ? "✓" : "—"} Event details extracted
          </li>
          <li className={page.outcome === "imported" ? "done" : ""}>
            {page.outcome === "imported" ? "✓" : "—"} Performances imported
          </li>
        </ol>
        {page.event && (
          <>
            <h3>What was extracted</h3>
            <div className="result-fields">
              {page.fields.map((field) => (
                <FieldCard key={field.key} field={field} />
              ))}
            </div>
            <section className="result-description">
              <h4>Description</h4>
              <p>{page.event.description}</p>
            </section>
            <div className="result-production-columns">
              <section>
                <h4>Artists and roles · {page.event.lineups.length}</h4>
                {page.event.lineups.length ? (
                  <ul>
                    {page.event.lineups.map((artist, index) => (
                      <li key={`${artist.artist_name}:${index}`}>
                        <strong>{artist.artist_name}</strong>
                        <span>{readableRole(artist.artist_type)}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No artists extracted.</p>
                )}
              </section>
              <section>
                <h4>Program · {page.event.setlists.length}</h4>
                {page.event.setlists.length ? (
                  <ol>
                    {page.event.setlists.map((work, index) => (
                      <li key={`${work.track}:${index}`}>
                        <strong>{work.track}</strong>
                        <span>{work.artist_name}</span>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p>
                    No works extracted. Check the source for a program or a
                    title and author.
                  </p>
                )}
              </section>
            </div>
            <section>
              <h4>Performances · {page.performances.length}</h4>
              <p className="result-help">
                Times are local to each venue. Each row is one performance.
              </p>
              <div className="result-table-scroll">
                <table className="result-performance-table">
                  <thead>
                    <tr>
                      <th>Date and time</th>
                      <th>Venue and hall</th>
                      <th>Import result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {page.performances.map((performance) => (
                      <tr key={performance.number}>
                        <td>
                          <strong>
                            {performance.date} · {performance.time}
                          </strong>
                          <small>{performance.timezone}</small>
                        </td>
                        <td>
                          <strong>
                            {performance.venue || "Venue not extracted"}
                          </strong>
                          <small>
                            {performance.hall || "Hall not extracted"}
                          </small>
                        </td>
                        <td>
                          <span
                            className={`result-outcome result-outcome-${performance.outcome === "failed" ? "failed" : ["created", "updated"].includes(performance.outcome) ? "imported" : "pending"}`}
                          >
                            {performance.reused
                              ? "Reused"
                              : performance.outcome === "created"
                                ? "Created"
                                : performance.outcome === "updated"
                                  ? "Updated"
                                  : performance.outcome === "failed"
                                    ? "Failed"
                                    : performance.outcome === "cancelled"
                                      ? "Stopped"
                                      : page.outcome === "imported"
                                        ? "Details unavailable"
                                        : "Waiting"}
                          </span>
                          <small>{performance.explanation}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </>
        )}
        {!page.event && page.scraped && page.outcome !== "unchanged" && (
          <p className="result-help">
            The page was scraped, but no validated event details were saved. No
            performances from this page were imported.
          </p>
        )}
        {page.outcome === "unchanged" && (
          <p className="result-help">
            The page was compared with its previous successful import.
            Extraction and import were skipped, so there is no new field report
            for this run.
          </p>
        )}
      </div>
    </details>
  );
}

export function RunResultsView({
  results,
  filter = "all",
  search = "",
  loading = false,
  onFilter,
  onSearch,
  onPage,
}: {
  results: RunResults;
  filter?: Filter;
  search?: string;
  loading?: boolean;
  onFilter?: (filter: Filter) => void;
  onSearch?: (search: string) => void;
  onPage?: (page: number) => void;
}) {
  const summary = results.summary;
  const pagination = results.pagination ?? {
    page: 1,
    pageSize: results.pages.length,
    total: results.pages.length,
  };
  const totalPages = Math.max(
    1,
    Math.ceil(pagination.total / Math.max(1, pagination.pageSize)),
  );
  return (
    <div className="run-results-view" aria-busy={loading}>
      <div className="result-run-heading">
        <span className={`result-run-status result-run-${summary.outcome}`}>
          {runLabels[summary.outcome]}
        </span>
        <p>
          {summary.createdPerformances} performances created ·{" "}
          {summary.updatedPerformances} updated · {summary.creditsCharged}{" "}
          credits charged
          {summary.reusedPerformances > 0 &&
            ` · ${summary.reusedPerformances} earlier imports reused`}
        </p>
      </div>
      {results.issue && (
        <div className="result-error">
          <div>
            <strong>{results.issue.explanation}</strong>
            <p>{results.issue.nextAction}</p>
          </div>
        </div>
      )}
      {results.refreshExtraction && (
        <p className="result-help">
          This run refreshes event details even when source pages are unchanged.
          Successfully imported event pages are charged as shown above.
        </p>
      )}
      <div className="result-stats">
        <div>
          <strong>
            {summary.pagesScraped}
            <small> / {summary.pagesFound}</small>
          </strong>
          <span>Source pages scraped</span>
        </div>
        <div>
          <strong>{summary.eventPages}</strong>
          <span>Event pages extracted</span>
        </div>
        <div>
          <strong>{summary.importedPerformances}</strong>
          <span>Confirmed performances imported</span>
        </div>
        <div className={summary.failedPages > 0 ? "result-stat-warning" : ""}>
          <strong>{summary.failedPages}</strong>
          <span>Pages with processing failures</span>
        </div>
      </div>
      <p className="result-help">
        One event page can contain several performances. Credits are charged per
        successfully imported event page. Scraping a page does not mean its
        event details were extracted or imported.
      </p>
      <div className="result-disposition">
        <span>{summary.skippedPages} pages skipped</span>
        <span>{summary.unchangedPages} unchanged</span>
        {summary.pendingPages > 0 && (
          <span>{summary.pendingPages} still processing</span>
        )}
        <span>{summary.pagesWithMissingFields} with fields to check</span>
        <span>{summary.creditsReleased} credits released</span>
      </div>
      <div className="result-toolbar">
        <label>
          Show
          <select
            value={filter}
            onChange={(event) => onFilter?.(event.target.value as Filter)}
          >
            <option value="all">All pages</option>
            <option value="attention">Failures or missing fields</option>
            <option value="failed">Processing failures</option>
            <option value="imported">Imported pages</option>
            <option value="unchanged">Unchanged pages</option>
            <option value="skipped">Skipped pages</option>
            <option value="pending">Still processing</option>
          </select>
        </label>
        <label>
          Find a page
          <input
            type="search"
            value={search}
            maxLength={200}
            placeholder="Event name or source URL"
            onChange={(event) => onSearch?.(event.target.value)}
          />
        </label>
      </div>
      <div className="result-page-list">
        {results.pages.map((page) => (
          <ResultPage
            key={`${results.runId}:${page.id}`}
            page={page}
            runId={results.runId}
          />
        ))}
        {results.pages.length === 0 && (
          <div className="result-empty">
            {summary.pagesFound === 0
              ? summary.outcome === "running"
                ? "No candidate pages have been found yet."
                : "No candidate pages were found in this run."
              : "No pages match these filters."}
          </div>
        )}
      </div>
      <div className="result-pagination">
        <span>
          {pagination.total} matching pages · Page {pagination.page} of{" "}
          {totalPages}
        </span>
        <div>
          <button
            type="button"
            disabled={loading || pagination.page <= 1}
            onClick={() => onPage?.(pagination.page - 1)}
          >
            Previous
          </button>
          <button
            type="button"
            disabled={loading || pagination.page >= totalPages}
            onClick={() => onPage?.(pagination.page + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export function RunResultsDialog({
  runId,
  onClose,
  onRetry,
  retryDisabled = false,
}: {
  runId: string;
  onClose: () => void;
  onRetry?: () => Promise<void>;
  retryDisabled?: boolean;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const [results, setResults] = useState<RunResults | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const previousFocus = document.activeElement;
    dialog.current?.showModal();
    return () => {
      dialog.current?.close();
      if (previousFocus instanceof HTMLElement && previousFocus.isConnected)
        previousFocus.focus();
    };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    const timeout = setTimeout(
      () => {
        setLoading(true);
        const params = new URLSearchParams({
          page: String(page),
          filter,
          search,
        });
        api<RunResults>(
          `/api/runs/${encodeURIComponent(runId)}/results?${params}`,
          { signal: controller.signal },
        )
          .then((value) => {
            if (!controller.signal.aborted) {
              setResults(value);
              setError(null);
            }
          })
          .catch((caught) => {
            if (!controller.signal.aborted)
              setError(
                caught instanceof Error
                  ? caught.message
                  : "Results could not be loaded.",
              );
          })
          .finally(() => {
            if (!controller.signal.aborted) setLoading(false);
          });
      },
      search ? 250 : 0,
    );
    return () => {
      clearTimeout(timeout);
      controller.abort();
    };
  }, [runId, page, filter, search, refresh]);
  useEffect(() => {
    if (results?.summary.outcome !== "running" || loading) return;
    const timeout = setTimeout(() => setRefresh((value) => value + 1), 5000);
    return () => clearTimeout(timeout);
  }, [results, loading]);
  return (
    <dialog
      className="results-dialog"
      ref={dialog}
      aria-labelledby="run-results-title"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <header className="results-dialog-header">
        <div>
          <p>Run results</p>
          <h2 id="run-results-title">
            {results?.jobName || "Loading results…"}
          </h2>
        </div>
        <button type="button" aria-label="Close results" onClick={onClose}>
          ×
        </button>
      </header>
      <div className="results-dialog-content">
        {error && (
          <div className="result-error" role="alert">
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setRefresh((value) => value + 1)}
            >
              Try again
            </button>
          </div>
        )}
        {!results && loading && (
          <p role="status">Loading page and performance results…</p>
        )}
        {results && (
          <RunResultsView
            results={results}
            filter={filter}
            search={search}
            loading={loading}
            onFilter={(value) => {
              setFilter(value);
              setPage(1);
            }}
            onSearch={(value) => {
              setSearch(value);
              setPage(1);
            }}
            onPage={setPage}
          />
        )}
      </div>
      <footer className="results-dialog-footer">
        <p>Retrying failed steps reuses successful imports.</p>
        <div>
          {onRetry && results && results.summary.failedPages > 0 && (
            <button
              type="button"
              className="action-primary"
              disabled={retrying || retryDisabled}
              onClick={async () => {
                setRetrying(true);
                try {
                  await onRetry();
                } finally {
                  setRetrying(false);
                }
              }}
            >
              {retrying ? "Starting retry…" : "Retry failed steps"}
            </button>
          )}
          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      </footer>
    </dialog>
  );
}
